import * as Api from '@ViewModels';
import { parseAsJson, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useUserQuery } from '../../../../../queries';
import { useUserAutocompleteQuery } from '../../../../../queries/User/useUserAutocompleteQuery';
import { buildOpportunityReportingApiFilter } from '../helpers/buildOpportunityReportingApiFilter';
import {
	compactOpportunityReportingFiltersForUrl,
	normalizeOpportunityReportingFilters,
	type OpportunityReportingNormalizedFilters,
	type OpportunityReportingUrlFilters,
	validateOpportunityReportingUrlFilters,
} from '../helpers/opportunityReportingUrlFilters';

const filtersParser = parseAsJson<OpportunityReportingUrlFilters>(validateOpportunityReportingUrlFilters).withDefault(
	{} as OpportunityReportingUrlFilters
);

export function useOpportunityReportingFilterBar({ boardId }: { boardId: string | null }) {
	const [filterParams, setFilterParams] = useQueryState('filters', filtersParser);
	const [ownerSearchFragment, setOwnerSearchFragment] = React.useState('');

	const normalizedFilters = useMemo(() => normalizeOpportunityReportingFilters(filterParams ?? {}), [filterParams]);

	const {
		includeArchived,
		filterStageId,
		ownerScope,
		specificOwnerUserId,
		creationDateFrom,
		creationDateTo,
		closeDateFrom,
		closeDateTo,
	} = normalizedFilters;

	const commitFilters = React.useCallback(
		(next: OpportunityReportingNormalizedFilters) => {
			void setFilterParams(compactOpportunityReportingFiltersForUrl(next));
		},
		[setFilterParams]
	);

	const patchFilters = React.useCallback(
		(patch: Partial<OpportunityReportingNormalizedFilters>) => {
			commitFilters({ ...normalizedFilters, ...patch });
		},
		[normalizedFilters, commitFilters]
	);

	const hasActiveReportFilters = useMemo(
		() =>
			includeArchived ||
			(Boolean(boardId) && Boolean(filterStageId)) ||
			ownerScope !== 'any' ||
			Boolean(specificOwnerUserId) ||
			Boolean(creationDateFrom) ||
			Boolean(creationDateTo) ||
			Boolean(closeDateFrom) ||
			Boolean(closeDateTo),
		[
			boardId,
			includeArchived,
			filterStageId,
			ownerScope,
			specificOwnerUserId,
			creationDateFrom,
			creationDateTo,
			closeDateFrom,
			closeDateTo,
		]
	);

	const handleClearReportFilters = React.useCallback(() => {
		void setFilterParams(null);
		setOwnerSearchFragment('');
	}, [setFilterParams]);

	const ownerAutocompleteQuery = useUserAutocompleteQuery({
		fragment: ownerSearchFragment,
		enabled: Boolean(ownerSearchFragment) && ownerScope === 'specific',
	});

	const selectedOwnerUserQuery = useUserQuery({
		id: specificOwnerUserId,
		enabled: Boolean(specificOwnerUserId),
	});

	React.useEffect(() => {
		if (ownerScope !== 'specific' || !specificOwnerUserId || !selectedOwnerUserQuery.data) {
			return;
		}
		if (!ownerSearchFragment) {
			setOwnerSearchFragment(getDisplayName(selectedOwnerUserQuery.data) || '');
		}
	}, [ownerScope, specificOwnerUserId, selectedOwnerUserQuery.data, ownerSearchFragment]);

	const ownerComboboxValue = selectedOwnerUserQuery.data ?? null;
	const ownerAutocompleteResults = ownerAutocompleteQuery.data?.values ?? [];

	const ownerComboboxItems = useMemo(() => {
		const selected = ownerComboboxValue;
		if (!selected) {
			return ownerAutocompleteResults;
		}
		if (ownerAutocompleteResults.some((u: Api.IUser) => u.id === selected.id)) {
			return ownerAutocompleteResults;
		}
		return [selected, ...ownerAutocompleteResults];
	}, [ownerAutocompleteResults, ownerComboboxValue]);

	const onOwnerScopeChange = React.useCallback(
		(next: OpportunityReportingNormalizedFilters['ownerScope']) => {
			patchFilters({
				ownerScope: next,
				specificOwnerUserId: next === 'specific' ? specificOwnerUserId : '',
			});
			if (next !== 'specific') {
				setOwnerSearchFragment('');
			}
		},
		[patchFilters, specificOwnerUserId]
	);

	const handleOwnerUserSelected = React.useCallback(
		(user: Api.IUser | null) => {
			if (user) {
				patchFilters({ ownerScope: 'specific', specificOwnerUserId: user.id });
				setOwnerSearchFragment(getDisplayName(user) || '');
			} else {
				patchFilters({ specificOwnerUserId: '' });
				setOwnerSearchFragment('');
			}
		},
		[patchFilters]
	);

	const apiFilterRequest = useMemo(
		() =>
			buildOpportunityReportingApiFilter({
				boardId: boardId ?? undefined,
				includeArchived,
				filterStageId,
				ownerScope,
				specificOwnerUserId,
				creationDateFrom,
				creationDateTo,
				closeDateFrom,
				closeDateTo,
			}),
		[
			boardId,
			includeArchived,
			filterStageId,
			ownerScope,
			specificOwnerUserId,
			creationDateFrom,
			creationDateTo,
			closeDateFrom,
			closeDateTo,
		]
	);

	return {
		normalizedFilters,
		includeArchived,
		filterStageId,
		ownerScope,
		specificOwnerUserId,
		creationDateFrom,
		creationDateTo,
		closeDateFrom,
		closeDateTo,
		patchFilters,
		hasActiveReportFilters,
		handleClearReportFilters,
		onOwnerScopeChange,
		handleOwnerUserSelected,
		ownerSearchFragment,
		setOwnerSearchFragment,
		ownerComboboxValue,
		ownerComboboxItems,
		ownerAutocompleteQuery,
		apiFilterRequest,
	};
}

export type UseOpportunityReportingFilterBarReturn = ReturnType<typeof useOpportunityReportingFilterBar>;
